import { Component } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AccessControlService } from "app/access-control/access-control-service";
import { AuthService } from "app/auth/auth.service";
import {
  AlertController,
  ModalController,
  ModalOptions,
  NavController,
  NavParams,
} from "ionic-angular";
import { FacilitiesService } from "providers/facilities-service/facilities.service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { UsersService } from "providers/users-service/users.service";
import { UtilityService } from "providers/utility-service/utility-service";

interface CareLevel {
  level: number;
  name: string;
  description: string;
}

type CareLevelList = CareLevel[];

interface SaveCareLevelsRequest {
  level: number;
  name: string;
  description: string;
}

type SaveCareLevelsRequestList = SaveCareLevelsRequest[];

@Component({
  selector: "manage-care-levels",
  templateUrl: "manage-care-levels.html",
})
export class ManageCareLevelsPage {
  cardHeader: string = "Facilities";
  touched: boolean = false;
  override: boolean = false;
  contactRemoved: boolean = false;
  careLevelsForm: FormGroup;
  careLevelsArray: FormArray;
  private originalLevels: Array<{ level: string; description: string }> = [];
  private initializing: boolean = true;

  constructor(
    protected accessControlService: AccessControlService,
    protected authSvc: AuthService,
    protected navBarSvc: NavBarServiceProvider,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected modalCtrl: ModalController,
    protected resourceManagementService: ResourceManagementService,
    protected alertController: AlertController,
    protected stateManagementService: StateManagementService,
    protected userService: UsersService,
    private formBuilder: FormBuilder,
    private facilitiesService: FacilitiesService,
    private utilityService: UtilityService
  ) {}

  ngOnInit() {
    this.touched = false;
    this.override = false;
    this.contactRemoved = false;
    this.saveEnabled = false;
    this.initializing = true;

    this.careLevelsArray = this.formBuilder.array([]);
    this.careLevelsForm = this.formBuilder.group({
      careLevels: this.careLevelsArray,
    });

    this.loadCareLevels();

    this.setupFormChangeDetection();
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  ionViewDidLoad() {
    this.stateManagementService.setAssessmentType("Company / Locations");
  }

  goBack() {
    if (this.navParams.data.parent) {
      if (this.navParams.data.parent.getFacilities) {
        this.navParams.data.parent.getFacilities();
      }
    }
    this.navCtrl.pop();
  }

  save() {
    if (!this.validateFields()) return;

    this.saveCareLevels();
  }

  addDefaultCareLevels() {
    const defaultLevels = [
      { level: "Level 1", description: "" },
      { level: "Level 2", description: "" },
      { level: "Level 3", description: "" },
      { level: "Level 4", description: "" },
      { level: "Level 5", description: "" },
    ];

    defaultLevels.forEach((level) => {
      this.careLevelsArray.push(
        this.formBuilder.group({
          level: [
            level.level.substring(0, 15),
            [Validators.required, Validators.maxLength(15)],
          ],
          description: [
            level.description.substring(0, 25),
            [Validators.maxLength(25)],
          ],
        })
      );
    });

    this.snapshotOriginalLevels();
    this.initializing = false;
    this.updateSaveEnabledState();
  }

  cancel() {
    if (!this.hasChanges()) {
      this.goBack();
      return;
    }

    const modalOptions: ModalOptions = {
      enableBackdropDismiss: false,
      cssClass: "delete-facility-confirmation-popup",
    };

    const modalPage = this.modalCtrl.create(
      "CancelFacilityPage",
      { title: "Discard Changes" },
      modalOptions
    );

    modalPage.onDidDismiss((data) => {
      if (data && data.cancel) {
        this.override = true;
        if (this.navParams.data.parent) {
          if (this.navParams.data.parent.getFacilities) {
            this.navParams.data.parent.getFacilities();
          }
        }
        this.navCtrl.pop();
      }
    });

    modalPage.present();
  }

  private showAlert = ({ title, message }) => {
    const alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  addLevel() {
    const newLevelGroup = this.formBuilder.group({
      level: ["", [Validators.required, Validators.maxLength(15)]],
      description: ["", [Validators.maxLength(25)]],
    });

    if (
      this.careLevelsArray &&
      this.careLevelsArray.length > 0 &&
      this.careLevelsArray.length < 30
    ) {
      this.careLevelsArray.push(newLevelGroup);
      this.touched = true;
    }

    setTimeout(() => {
      this.updateSaveEnabledState();
    }, 0);
  }

  removeLevel(index: number) {
    this.facilitiesService.deleteCareLevels(index + 1).subscribe(
      (response) => {
        if (response > 0) {
          this.showAlert({
            title: "Error",
            message:
              "This care level cannot be deleted because Room and Board claims are associated with it.",
          });
        } else {
          this.careLevelsArray.removeAt(index);
          this.touched = true;

          setTimeout(() => {
            this.updateSaveEnabledState();
          }, 0);
        }
      },
      (error) => {
        this.onSaveError(error);
      }
    );
  }

  onLevelInputChanged(
    index: number,
    field: "level" | "description",
    value: string
  ) {
    const maxLength = field === "level" ? 15 : 25;
    const trimmed = (value || "").substring(0, maxLength);
    this.careLevelsArray.at(index).get(field)?.setValue(trimmed);
    this.touched = true;

    setTimeout(() => {
      this.updateSaveEnabledState();
    }, 0);
  }

  hasDuplicateNames(): boolean {
    const names = this.careLevelsArray.controls
      .map((ctrl) => (ctrl.get("level")?.value || "").trim())
      .filter((n) => n.length > 0);
    const seen = new Set<string>();
    for (const n of names) {
      if (seen.has(n.toLowerCase())) return true;
      seen.add(n.toLowerCase());
    }
    return false;
  }

  isNameDuplicate(index: number): boolean {
    const current = (this.careLevelsArray.at(index).get("level")?.value || "")
      .trim()
      .toLowerCase();
    if (!current) return false;
    let count = 0;
    for (const ctrl of this.careLevelsArray.controls) {
      if ((ctrl.get("level")?.value || "").trim().toLowerCase() === current)
        count++;
      if (count > 1) return true;
    }
    return false;
  }

  hasChanges(): boolean {
    if (this.careLevelsArray.length !== this.originalLevels.length) {
      return true;
    }
    for (let i = 0; i < this.careLevelsArray.length; i++) {
      const current = this.careLevelsArray.at(i);
      const original = this.originalLevels[i];
      const currentLevel = (current.get("level")?.value || "").trim();
      const originalLevel = (original.level || "").trim();
      const currentDesc = (current.get("description")?.value || "").trim();
      const originalDesc = (original.description || "").trim();

      if (currentLevel !== originalLevel) {
        return true;
      }
      if (currentDesc !== originalDesc) {
        return true;
      }
    }
    return false;
  }

  private snapshotOriginalLevels() {
    this.originalLevels = this.careLevelsArray.controls.map((ctrl) => ({
      level: (ctrl.get("level")?.value || "").trim(),
      description: (ctrl.get("description")?.value || "").trim(),
    }));
  }

  saveEnabled = false;

  validateFields(): boolean {
    for (let i = 0; i < this.careLevelsArray.length; i++) {
      const levelControl = this.careLevelsArray.at(i).get("level");
      if (
        !levelControl ||
        !levelControl.value ||
        (levelControl.value + "").trim().length === 0
      ) {
        this.showAlert({
          title: "Error",
          message: "Please fill in all required care level names.",
        });
        return false;
      }
      const descriptionControl = this.careLevelsArray.at(i).get("description");
      if (!descriptionControl || descriptionControl.invalid) {
        this.showAlert({
          title: "Error",
          message: "Please fix validation errors in descriptions.",
        });
        return false;
      }
    }

    if (this.hasDuplicateNames()) {
      this.showAlert({
        title: "Error",
        message: "Please remove duplicate care level names.",
      });
      return false;
    }

    return true;
  }

  private validateFieldsSilent(): boolean {
    for (let i = 0; i < this.careLevelsArray.length; i++) {
      const levelControl = this.careLevelsArray.at(i).get("level");
      if (
        !levelControl ||
        levelControl.invalid ||
        (levelControl.value + "").trim().length === 0
      ) {
        return false;
      }
      const descriptionControl = this.careLevelsArray.at(i).get("description");
      if (!descriptionControl || descriptionControl.invalid) {
        return false;
      }
    }

    if (this.hasDuplicateNames()) {
      return false;
    }

    return true;
  }

  setupFormChangeDetection() {
    this.careLevelsForm.valueChanges.subscribe(() => {
      if (this.initializing) {
        this.saveEnabled = false;
        return;
      }
      this.updateSaveEnabledState();
    });

    this.careLevelsArray.valueChanges.subscribe(() => {
      if (this.initializing) {
        this.saveEnabled = false;
        return;
      }
      this.updateSaveEnabledState();
    });
  }

  private updateSaveEnabledState() {
    const hasChanges = this.hasChanges();
    const isValid = this.validateFieldsSilent();
    this.saveEnabled = hasChanges && isValid;
  }

  private loadCareLevels() {
    this.utilityService.increaseSpinnerCount("Loading care levels...");

    this.facilitiesService.getCareLevels().subscribe(
      (response: CareLevelList) => {
        this.utilityService.decreaseSpinnerCount();

        if (response && response.length > 0) {
          this.populateFormWithApiData(response);
        } else {
          this.addDefaultCareLevels();
        }
      },
      (error) => {
        this.utilityService.decreaseSpinnerCount();
        this.addDefaultCareLevels();
      }
    );
  }

  private populateFormWithApiData(careLevels: CareLevelList) {
    while (this.careLevelsArray.length !== 0) {
      this.careLevelsArray.removeAt(0);
    }

    const sortedLevels = careLevels.sort((a, b) => a.level - b.level);

    sortedLevels.forEach((level) => {
      const levelGroup = this.formBuilder.group({
        level: [
          level.name ? level.name.substring(0, 15) : "",
          [Validators.required, Validators.maxLength(15)],
        ],
        description: [
          level.description ? level.description.substring(0, 25) : "",
          [Validators.maxLength(25)],
        ],
      });
      this.careLevelsArray.push(levelGroup);
    });

    this.originalLevels = sortedLevels.map((level) => ({
      level: level.name,
      description: level.description,
    }));

    this.updateSaveEnabledState();
    this.initializing = false;
  }

  private saveCareLevels() {
    this.utilityService.increaseSpinnerCount("Saving care levels...");

    const careLevelsToSave: SaveCareLevelsRequestList = [];

    for (let i = 0; i < this.careLevelsArray.length; i++) {
      const levelGroup = this.careLevelsArray.at(i);
      careLevelsToSave.push({
        level: i + 1,
        name: levelGroup.get("level")?.value || "",
        description: levelGroup.get("description")?.value || "",
      });
    }

    this.facilitiesService.saveCareLevels(careLevelsToSave).subscribe(
      (response: CareLevelList) => {
        this.onSaveSuccess(response);
      },
      (error) => {
        this.onSaveError(error);
      }
    );
  }

  private onSaveSuccess = (data: CareLevelList) => {
    setTimeout(() => {
      this.utilityService.decreaseSpinnerCount();
      this.saveEnabled = false;

      this.originalLevels = data.map((level) => ({
        level: level.name,
        description: level.description,
      }));

      this.updateSaveEnabledState();

      this.onSaveSuccessAlert();
    }, 2000);
  };

  private onSaveSuccessAlert = () => {
    this.showAlert({
      title: "Success",
      message: "Care levels saved successfully!",
    });
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();

    if (error.error && Array.isArray(error.error)) {
      const errorMessages = error.error
        .map((err: any) => err.message)
        .join(", ");
      this.showAlert({
        title: "Error",
        message: `Validation Error: ${errorMessages}`,
      });
    } else {
      this.showAlert({
        title: "Error",
        message: "Error saving care levels. Please try again.",
      });
    }
  };

  allowOnlyNumbersAndLetters(e) {
    var regex = new RegExp("^[a-zA-Z0-9 ]+$");
    var str = !e.key ? String.fromCharCode(e.which) : e.key;
    if (!regex.test(str)) {
      e.preventDefault();
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/facilities-view/manage-care-levels/manage-care-levels.ts